import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';
import { MODEL_DISPLAY_NAMES } from '@/lib/ai-agent/model-constants';

type UsageEventRow = Pick<
  Database['public']['Tables']['usage_events']['Row'],
  | 'id'
  | 'tool_name'
  | 'model_id'
  | 'input_tokens'
  | 'cached_input_tokens'
  | 'output_tokens'
  | 'cached_output_tokens'
  | 'cost_micro_usd'
  | 'error_code'
  | 'created_at'
>;

/** One row of the account's activity log — the Jam-shaped Date/Category/Name/Amount
 *  table on Settings → Usage. `costMicroUsd` is the REAL per-event cost (unlike the
 *  header "Credits available" stat, which uses a published flat rate — see
 *  `usage-capacity.ts`'s `buildPlanCreditSummary` and
 *  docs/planning/usage-credits-redesign-2026-08-18.md §3.1 for why the two
 *  intentionally differ). */
export interface UsageLogItem {
  id: string;
  createdAt: string;
  category: string;
  name: string;
  costMicroUsd: number;
}

/** One day's activity count for the "Activity" bar chart — zero-filled across the
 *  period so the chart has a continuous axis even on days with nothing logged. */
export interface UsageDayCount {
  day: string; // YYYY-MM-DD
  count: number;
}

export interface UsageSummary {
  since: string;
  until: string;
  events: number;
  images: number;
  videos: number;
  mediaEvents: number;
  scheduledPosts: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  lastUpdated: string | null;
  /** Most recent activity first, capped at `LOG_DISPLAY_LIMIT` — see `totalLoggable`
   *  for whether more exist than are shown. */
  log: UsageLogItem[];
  /** Count of non-errored rows this period, before the display cap — lets the UI say
   *  "showing latest N of totalLoggable" instead of silently truncating. */
  totalLoggable: number;
  /** Zero-filled from `since` to `min(until, now)` — built from the same up-to-1000
   *  fetched rows as `log`/`events`, not a second query. */
  dailySeries: UsageDayCount[];
}

const LOG_DISPLAY_LIMIT = 50;
const DAY_MS = 24 * 60 * 60 * 1000;

function isImageEvent(row: UsageEventRow): boolean {
  const tool = row.tool_name?.toLowerCase() ?? '';
  const model = row.model_id.toLowerCase();
  return tool.includes('image') || model.includes('image') || model.includes('imagen');
}

function isVideoEvent(row: UsageEventRow): boolean {
  const tool = row.tool_name?.toLowerCase() ?? '';
  const model = row.model_id.toLowerCase();
  return tool.includes('video') || model.includes('veo');
}

/** Substring match against `MODEL_DISPLAY_NAMES` (same longest-key-wins approach as
 *  `media-pricing.ts`'s `resolveModelKey`, kept separate — that one resolves a
 *  pricing rate, this one resolves a user-facing label) so a gateway-prefixed or
 *  versioned model id (`google/gemini-3-pro-image`) still finds its display name.
 *  Falls back to the raw id rather than throwing — a log row must always render. */
function humanModelName(modelId: string): string {
  const lower = modelId.toLowerCase();
  const keys = Object.keys(MODEL_DISPLAY_NAMES).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (lower.includes(key.toLowerCase())) return MODEL_DISPLAY_NAMES[key];
  }
  return modelId;
}

/** Category/Name pair for one activity-log row, matching Jam's own log grammar
 *  (`Image generation` / model name, `Tool call` / tool name, `Chat message` /
 *  `Chat message`) — see the redesign doc §3.2. */
function categorizeEvent(row: UsageEventRow): { category: string; name: string } {
  if (isImageEvent(row))
    return { category: 'Image generation', name: humanModelName(row.model_id) };
  if (isVideoEvent(row))
    return { category: 'Video generation', name: humanModelName(row.model_id) };
  if (row.tool_name) return { category: 'Tool call', name: row.tool_name };
  return { category: 'Chat message', name: 'Chat message' };
}

/** Zero-fills day-by-day from `since` to `min(until, now)` — a period end in the
 *  future (the common case, since it's the plan's renewal date) must not produce
 *  empty future days on the chart's right edge, mirroring the admin activity
 *  endpoint's same trailing-window pattern. */
function buildDailySeries(rows: UsageEventRow[], since: string, until: string): UsageDayCount[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const day = row.created_at.slice(0, 10);
    counts.set(day, (counts.get(day) ?? 0) + 1);
  }

  const start = new Date(since);
  const end = new Date(Math.min(new Date(until).getTime(), Date.now()));
  const series: UsageDayCount[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += DAY_MS) {
    const day = new Date(t).toISOString().slice(0, 10);
    series.push({ day, count: counts.get(day) ?? 0 });
  }
  return series;
}

class UsageService extends BaseService {
  async getCurrentPeriodSummary(args: {
    since: string;
    until: string;
  }): Promise<ServiceResponse<UsageSummary>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('usage_events')
        .select(
          // A literal string, not `[...].join(',')` — the join produces a widened
          // `string`, which loses the literal type Supabase's `.select()` overload
          // needs to statically parse the column list. With the widened type it
          // silently falls back to `GenericStringError[]`, and `id`/`cost_micro_usd`
          // (added for the credits redesign) were the fields that finally tipped it
          // into that fallback rather than a real row type.
          'id, tool_name, model_id, input_tokens, cached_input_tokens, output_tokens, cached_output_tokens, cost_micro_usd, error_code, created_at',
        )
        .gte('created_at', args.since)
        .lt('created_at', args.until)
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) throw ServiceError.fromPostgrestError(error);

      const { count: scheduledPosts, error: scheduledPostsError } = await this.client
        .from('social_posts')
        .select('id', { count: 'exact', head: true })
        .gte('scheduled_at', args.since)
        .lt('scheduled_at', args.until);

      if (scheduledPostsError) throw ServiceError.fromPostgrestError(scheduledPostsError);

      const rows = (data ?? []).filter((row) => !row.error_code) as UsageEventRow[];
      const images = rows.filter(isImageEvent).length;
      const videos = rows.filter(isVideoEvent).length;
      const log: UsageLogItem[] = rows.slice(0, LOG_DISPLAY_LIMIT).map((row) => {
        const { category, name } = categorizeEvent(row);
        const isMetered = isImageEvent(row) || isVideoEvent(row);
        return {
          id: row.id,
          createdAt: row.created_at,
          category,
          name,
          // Every row carries a REAL cost_micro_usd — recordUsageEvent() prices
          // text/tool-call LLM steps too, not just media generation. Per
          // pricing.ts ("text/chat/ad-copy generation is never metered, on any
          // tier") and the redesign doc §4 Q1, only image/video rows should ever
          // show a credit amount; forcing 0 here is what makes Chat
          // message/Tool call rows render "Free" downstream instead of silently
          // billing for activity the plan explicitly doesn't meter.
          costMicroUsd: isMetered ? row.cost_micro_usd : 0,
        };
      });

      return {
        since: args.since,
        until: args.until,
        events: rows.length,
        images,
        videos,
        mediaEvents: images + videos,
        scheduledPosts: scheduledPosts ?? 0,
        totalInputTokens: rows.reduce(
          (sum, row) => sum + row.input_tokens + row.cached_input_tokens,
          0,
        ),
        totalOutputTokens: rows.reduce(
          (sum, row) => sum + row.output_tokens + row.cached_output_tokens,
          0,
        ),
        lastUpdated: rows[0]?.created_at ?? null,
        log,
        totalLoggable: rows.length,
        dailySeries: buildDailySeries(rows, args.since, args.until),
      };
    }, 'Failed to load usage summary');
  }
}

export const usageService = new UsageService();
